import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  MenuItem,
  Pagination,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import StorefrontIcon from "@mui/icons-material/Storefront";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import RefreshIcon from "@mui/icons-material/Refresh";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CurrencyRupeeIcon from "@mui/icons-material/CurrencyRupee";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import ShoppingCartCheckoutOutlinedIcon from "@mui/icons-material/ShoppingCartCheckoutOutlined";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import CalculateOutlinedIcon from "@mui/icons-material/CalculateOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import HighlightOffOutlinedIcon from "@mui/icons-material/HighlightOffOutlined";
import { API } from '../../../utils/Api';

const formatMonthYear = (value) => {
  if (!value) return "";
  const [year, month] = value.split("-");
  return `${month}-${year}`;
};

const monthYearToInputValue = (value) => {
  if (!value || !value.includes("-")) return currentMonth();
  const [month, year] = value.split("-");
  return `${year}-${month}`;
};

const formatDate = (date) => {
  if (!date) return "";
  const d = new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const currentMonth = () => {
  const d = new Date();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  return `${d.getFullYear()}-${month}`;
};

const FoodDeliveryApp = () => {
  const accessToken = localStorage.getItem("token");

  const [loading, setLoading] = useState(false);
  const [salesList, setSalesList] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);

  const [filterMonth, setFilterMonth] = useState(currentMonth());
  const [formMonth, setFormMonth] = useState(currentMonth());

  const [formData, setFormData] = useState({
    app: "ZOMATO",
    saleFromDate: "",
    saleToDate: "",
    monthYear: formatMonthYear(currentMonth()),
    totalSaleValue: "",
    deliveredCount: "",
    cancelledCount: "",
    totalOrders: "",
    deductions: "",
    totalPayouts: "",
  });

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);

  const [salesId, setSalesId] = useState("");
  const [deleteBtnLoading, setDeleteBtnLoading] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const apps = ["ZOMATO", "SWIGGY"];

  const handleError = (error) => {
    let errorMessage = "Something went wrong. Please try again.";

    if (error?.response) {
      if (error.response?.status === 406) {
        errorMessage =
          error?.response?.data?.error?.message ||
          error?.response?.data?.error ||
          errorMessage;
      } else if (error.response?.status === 412) {
        errorMessage = error?.response?.data?.data?.message || errorMessage;
      } else if (error.response?.status === 500) {
        errorMessage = error?.response?.data?.error || errorMessage;
      }
    }

    toast.error(errorMessage, {
      position: "top-right",
      autoClose: 3000,
      theme: "light",
    });
  };

  const getFoodDeliveryAppSales = async (
    page = currentPage,
    limit = itemsPerPage
  ) => {
    try {
      setLoading(true);

      const headers = {
        Authorization: `Bearer ${accessToken}`,
      };

      const monthYear = formatMonthYear(filterMonth);

      const params = {
        page,
        limit,
        monthYear,
      };

      const response = await axios.get(`${API.BASE_URL}food-delivery-apps-sales/list`, {
        headers,
        // params,
      });

      if (response?.data) {
        if (response?.data?.data?.length > 0) {
          setSalesList(response.data.data);

          if (response?.data?.pagination) {
            setTotalPages(response.data.pagination.totalPages || 1);
            setTotalRecords(response.data.pagination.totalRecords || 0);
            setCurrentPage(response.data.pagination.page || 1);
          } else {
            setTotalPages(Math.ceil(response.data.data.length / limit) || 1);
            setTotalRecords(response.data.data.length || 0);
          }
        } else {
          setSalesList([]);
          setTotalPages(1);
          setTotalRecords(0);
          setCurrentPage(1);

          toast.info("No records found.", {
            position: "top-right",
            autoClose: 3000,
            theme: "light",
          });
        }
      } else {
        setSalesList([]);
        setTotalPages(1);
        setTotalRecords(0);
        setCurrentPage(1);
      }
    } catch (error) {
      console.error("Error fetching sales data:", error);
      handleError(error);
      setTotalPages(1);
      setTotalRecords(0);
      setCurrentPage(1);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleDateChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleFilterMonthChange = (e) => {
    setFilterMonth(e.target.value);
    setCurrentPage(1);
  };

  const handleFormMonthChange = (e) => {
    const value = e.target.value;
    setFormMonth(value);
    setFormData((prev) => ({
      ...prev,
      monthYear: formatMonthYear(value),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      setLoading(true);

      const headers = {
        Authorization: `Bearer ${accessToken}`,
      };

      const payload = {
        ...formData,
        totalSaleValue: parseFloat(formData.totalSaleValue || 0),
        deliveredCount: parseInt(formData.deliveredCount || 0, 10),
        cancelledCount: parseInt(formData.cancelledCount || 0, 10),
        totalOrders: parseInt(formData.totalOrders || 0, 10),
        deductions: parseFloat(formData.deductions || 0),
        totalPayouts: parseFloat(formData.totalPayouts || 0),
        monthYear: formData.monthYear,
      };

      let response;
      if (editingRecord) {
        response = await axios.put(
          `food-delivery-apps-sales/`,
          {
            ...payload,
            _id: editingRecord._id,
          },
          { headers }
        );
      } else {
        response = await axios.post(`food-delivery-apps-sales/`, payload, {
          headers,
        });
      }

      if (response?.data?.status === true) {
        toast.success(
          editingRecord
            ? "Record updated successfully"
            : "Record created successfully",
          {
            position: "top-right",
            autoClose: 3000,
            theme: "light",
          }
        );

        setShowModal(false);
        setEditingRecord(null);
        resetForm();
        getFoodDeliveryAppSales(currentPage, itemsPerPage);
      }
    } catch (error) {
      handleError(error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDeleteModal = (id) => {
    setSalesId(id);
    setShowDeleteConfirm(true);
  };

  const handleDelete = async () => {
    setDeleteBtnLoading(true);

    try {
      setLoading(true);

      const headers = {
        Authorization: `Bearer ${accessToken}`,
      };

      const response = await axios.delete(`food-delivery-apps-sales`, {
        headers,
        data: { id: salesId },
      });

      if (response?.data?.status === true) {
        toast.success("Record deleted successfully", {
          position: "top-right",
          autoClose: 3000,
          theme: "light",
        });

        setShowDeleteConfirm(false);
        setSalesId("");
        getFoodDeliveryAppSales(currentPage, itemsPerPage);
      }
    } catch (error) {
      handleError(error);
    } finally {
      setLoading(false);
      setDeleteBtnLoading(false);
    }
  };

  const handleEdit = (record) => {
    setEditingRecord(record);

    const recordMonth = record?.monthYear
      ? monthYearToInputValue(record.monthYear)
      : currentMonth();

    setFormMonth(recordMonth);

    setFormData({
      app: record.app || "ZOMATO",
      saleFromDate: record.saleFromDate ? formatDate(record.saleFromDate) : "",
      saleToDate: record.saleToDate ? formatDate(record.saleToDate) : "",
      monthYear: record.monthYear || formatMonthYear(currentMonth()),
      totalSaleValue: record.totalSaleValue || "",
      deliveredCount: record.deliveredCount || "",
      cancelledCount: record.cancelledCount || "",
      totalOrders: record.totalOrders || "",
      deductions: record.deductions || "",
      totalPayouts: record.totalPayouts || "",
    });

    setShowModal(true);
  };

  const resetForm = () => {
    setFormMonth(currentMonth());
    setFormData({
      app: "ZOMATO",
      saleFromDate: "",
      saleToDate: "",
      monthYear: formatMonthYear(currentMonth()),
      totalSaleValue: "",
      deliveredCount: "",
      cancelledCount: "",
      totalOrders: "",
      deductions: "",
      totalPayouts: "",
    });
  };

  const handlePageChange = (_, pageNumber) => {
    setCurrentPage(pageNumber);
    getFoodDeliveryAppSales(pageNumber, itemsPerPage);
  };

  const stats = useMemo(() => {
    const totalSales = salesList.reduce(
      (sum, item) => sum + parseFloat(item.totalSaleValue || 0),
      0
    );
    const totalOrders = salesList.reduce(
      (sum, item) => sum + parseInt(item.totalOrders || 0, 10),
      0
    );
    const totalPayouts = salesList.reduce(
      (sum, item) => sum + parseFloat(item.totalPayouts || 0),
      0
    );
    const totalDeductions = salesList.reduce(
      (sum, item) => sum + parseFloat(item.deductions || 0),
      0
    );
    const deliveredCount = salesList.reduce(
      (sum, item) => sum + parseInt(item.deliveredCount || 0, 10),
      0
    );
    const cancelledCount = salesList.reduce(
      (sum, item) => sum + parseInt(item.cancelledCount || 0, 10),
      0
    );
    const successRate =
      totalOrders > 0
        ? ((deliveredCount / totalOrders) * 100).toFixed(2)
        : 0;

    return {
      totalSales,
      totalOrders,
      totalPayouts,
      totalDeductions,
      deliveredCount,
      cancelledCount,
      successRate,
    };
  }, [salesList]);

  useEffect(() => {
    getFoodDeliveryAppSales(1, itemsPerPage);
  }, [filterMonth]);

  const getAppChipColor = (app) => {
    switch (app) {
      case "ZOMATO":
        return "error";
      case "SWIGGY":
        return "warning";
      default:
        return "default";
    }
  };

  return (
    <Box sx={{ bgcolor: "#f5f7fb", minHeight: "100vh", paddingTop: '100px' }}>
      <Container maxWidth="xl">
        <Paper elevation={2} sx={{ p: 3, borderRadius: 3 }}>
          <Stack
            direction={{ xs: "column", md: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "flex-start", md: "center" }}
            spacing={2}
            mb={3}
          >
            <Box>
              <Stack direction="row" alignItems="center" spacing={1}>
                <StorefrontIcon />
                <Typography variant="h5" fontWeight={700}>
                  Food Delivery App Sales
                </Typography>
              </Stack>
              <Typography variant="body2" color="text.secondary" mt={0.5}>
                Manage your food delivery app sales and payouts
              </Typography>
            </Box>

            <Stack direction="row" spacing={1.5} flexWrap="wrap">
              <Button
                variant="contained"
                startIcon={<AddCircleOutlineIcon />}
                onClick={() => {
                  setEditingRecord(null);
                  resetForm();
                  setShowModal(true);
                }}
              >
                Add Record
              </Button>

              <Button
                variant="outlined"
                onClick={() => getFoodDeliveryAppSales(currentPage, itemsPerPage)}
                disabled={loading}
                sx={{ minWidth: 44 }}
              >
                <RefreshIcon
                  sx={{
                    animation: loading ? "spin 1s linear infinite" : "none",
                  }}
                />
              </Button>
            </Stack>
          </Stack>

          <Divider sx={{ mb: 3 }} />

          <Grid container spacing={2.5} mb={3}>
            <Grid item xs={12} sm={6} md={4} lg={2}>
              <Card elevation={1} sx={{ borderRadius: 3, height: "100%" }}>
                <CardContent sx={{ textAlign: "center" }}>
                  <CurrencyRupeeIcon color="success" sx={{ fontSize: 28, mb: 1 }} />
                  <Typography variant="body2" color="text.secondary">
                    Total Sales
                  </Typography>
                  <Typography variant="h6" color="success.main" fontWeight={700}>
                    ₹
                    {stats.totalSales.toLocaleString("en-IN", {
                      minimumFractionDigits: 2,
                    })}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={4} lg={2}>
              <Card elevation={1} sx={{ borderRadius: 3, height: "100%" }}>
                <CardContent sx={{ textAlign: "center" }}>
                  <PaymentsOutlinedIcon color="warning" sx={{ fontSize: 28, mb: 1 }} />
                  <Typography variant="body2" color="text.secondary">
                    Total Payouts
                  </Typography>
                  <Typography variant="h6" color="warning.main" fontWeight={700}>
                    ₹
                    {stats.totalPayouts.toLocaleString("en-IN", {
                      minimumFractionDigits: 2,
                    })}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={4} lg={2}>
              <Card elevation={1} sx={{ borderRadius: 3, height: "100%" }}>
                <CardContent sx={{ textAlign: "center" }}>
                  <ShoppingCartCheckoutOutlinedIcon
                    color="primary"
                    sx={{ fontSize: 28, mb: 1 }}
                  />
                  <Typography variant="body2" color="text.secondary">
                    Delivered
                  </Typography>
                  <Typography variant="h6" color="primary.main" fontWeight={700}>
                    {stats.deliveredCount}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={4} lg={2}>
              <Card elevation={1} sx={{ borderRadius: 3, height: "100%" }}>
                <CardContent sx={{ textAlign: "center" }}>
                  <CancelOutlinedIcon color="error" sx={{ fontSize: 28, mb: 1 }} />
                  <Typography variant="body2" color="text.secondary">
                    Cancelled
                  </Typography>
                  <Typography variant="h6" color="error.main" fontWeight={700}>
                    {stats.cancelledCount}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={4} lg={2}>
              <Card elevation={1} sx={{ borderRadius: 3, height: "100%" }}>
                <CardContent sx={{ textAlign: "center" }}>
                  <CalculateOutlinedIcon color="info" sx={{ fontSize: 28, mb: 1 }} />
                  <Typography variant="body2" color="text.secondary">
                    Total Orders
                  </Typography>
                  <Typography variant="h6" color="info.main" fontWeight={700}>
                    {stats.totalOrders}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={4} lg={2}>
              <TextField
                label="Select Month"
                type="month"
                value={filterMonth}
                onChange={handleFilterMonthChange}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>

          <Card elevation={1} sx={{ borderRadius: 3 }}>
            <CardContent>
              {loading ? (
                <Box textAlign="center" py={6}>
                  <CircularProgress />
                  <Typography mt={2} color="text.secondary">
                    Loading data...
                  </Typography>
                </Box>
              ) : salesList.length === 0 ? (
                <Alert severity="info" sx={{ justifyContent: "center" }}>
                  No sales records found for the selected month.
                </Alert>
              ) : (
                <>
                  <TableContainer component={Paper} elevation={0}>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>#</TableCell>
                          <TableCell>App</TableCell>
                          <TableCell align="center">Month</TableCell>
                          <TableCell align="right">Sales Value</TableCell>
                          <TableCell align="center">Orders</TableCell>
                          <TableCell align="right">Deductions</TableCell>
                          <TableCell align="right">Payouts</TableCell>
                          <TableCell align="center">Actions</TableCell>
                        </TableRow>
                      </TableHead>

                      <TableBody>
                        {salesList.map((record, index) => (
                          <TableRow key={record._id} hover>
                            <TableCell>
                              {(currentPage - 1) * itemsPerPage + index + 1}
                            </TableCell>

                            <TableCell>
                              <Chip
                                label={record.app}
                                color={getAppChipColor(record.app)}
                                size="small"
                              />
                            </TableCell>

                            <TableCell align="center">
                              {record.monthYear}
                            </TableCell>

                            <TableCell
                              align="right"
                              sx={{ fontWeight: 700, color: "success.main" }}
                            >
                              ₹
                              {parseFloat(record.totalSaleValue || 0).toLocaleString(
                                "en-IN",
                                { minimumFractionDigits: 2 }
                              )}
                            </TableCell>

                            <TableCell align="center">
                              <Stack alignItems="center" spacing={0.3}>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    color: "success.main",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 0.5,
                                  }}
                                >
                                  <CheckCircleOutlineIcon fontSize="inherit" />
                                  {record.deliveredCount || 0}
                                </Typography>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    color: "error.main",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 0.5,
                                  }}
                                >
                                  <HighlightOffOutlinedIcon fontSize="inherit" />
                                  {record.cancelledCount || 0}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  Total: {record.totalOrders || 0}
                                </Typography>
                              </Stack>
                            </TableCell>

                            <TableCell align="right" sx={{ color: "error.main" }}>
                              ₹
                              {parseFloat(record.deductions || 0).toLocaleString(
                                "en-IN",
                                { minimumFractionDigits: 2 }
                              )}
                            </TableCell>

                            <TableCell
                              align="right"
                              sx={{ fontWeight: 700, color: "primary.main" }}
                            >
                              ₹
                              {parseFloat(record.totalPayouts || 0).toLocaleString(
                                "en-IN",
                                { minimumFractionDigits: 2 }
                              )}
                            </TableCell>

                            <TableCell align="center">
                              <IconButton
                                color="primary"
                                onClick={() => handleEdit(record)}
                              >
                                <EditOutlinedIcon />
                              </IconButton>
                              <IconButton
                                color="error"
                                onClick={() => handleOpenDeleteModal(record._id)}
                              >
                                <DeleteOutlineIcon />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  {totalPages > 1 && (
                    <Stack
                      direction={{ xs: "column", md: "row" }}
                      justifyContent="space-between"
                      alignItems={{ xs: "flex-start", md: "center" }}
                      spacing={2}
                      mt={3}
                    >
                      <Stack
                        direction="row"
                        spacing={2}
                        alignItems="center"
                        flexWrap="wrap"
                      >
                        <TextField
                          select
                          label="Rows"
                          size="small"
                          value={itemsPerPage}
                          onChange={(e) => {
                            const limit = parseInt(e.target.value, 10);
                            setItemsPerPage(limit);
                            setCurrentPage(1);
                            getFoodDeliveryAppSales(1, limit);
                          }}
                          sx={{ minWidth: 110 }}
                        >
                          {[5, 10, 20, 50, 100].map((size) => (
                            <MenuItem key={size} value={size}>
                              {size}
                            </MenuItem>
                          ))}
                        </TextField>

                        <Typography variant="body2" color="text.secondary">
                          Showing {(currentPage - 1) * itemsPerPage + 1} to{" "}
                          {Math.min(currentPage * itemsPerPage, totalRecords)} of{" "}
                          {totalRecords} records
                        </Typography>
                      </Stack>

                      <Pagination
                        count={totalPages}
                        page={currentPage}
                        onChange={handlePageChange}
                        color="primary"
                        shape="rounded"
                      />
                    </Stack>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </Paper>
      </Container>

      <Dialog
        open={showModal}
        onClose={() => {
          setShowModal(false);
          setEditingRecord(null);
          resetForm();
        }}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>
          {editingRecord ? "Edit Sales Record" : "Add New Sales Record"}
        </DialogTitle>

        <Box component="form" onSubmit={handleSubmit}>
          <DialogContent dividers>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  select
                  name="app"
                  label="Delivery App"
                  value={formData.app}
                  onChange={handleInputChange}
                  required
                  fullWidth
                >
                  {apps.map((app) => (
                    <MenuItem key={app} value={app}>
                      {app}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  label="Month Year"
                  type="month"
                  value={formMonth}
                  onChange={handleFormMonthChange}
                  required
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  label="Total Sales Value (₹)"
                  type="number"
                  name="totalSaleValue"
                  value={formData.totalSaleValue}
                  onChange={handleInputChange}
                  required
                  fullWidth
                  inputProps={{ step: "0.01", min: 0 }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  label="Deductions (₹)"
                  type="number"
                  name="deductions"
                  value={formData.deductions}
                  onChange={handleInputChange}
                  required
                  fullWidth
                  inputProps={{ step: "0.01", min: 0 }}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  label="Delivered Count"
                  type="number"
                  name="deliveredCount"
                  value={formData.deliveredCount}
                  onChange={handleInputChange}
                  required
                  fullWidth
                  inputProps={{ min: 0 }}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  label="Cancelled Count"
                  type="number"
                  name="cancelledCount"
                  value={formData.cancelledCount}
                  onChange={handleInputChange}
                  required
                  fullWidth
                  inputProps={{ min: 0 }}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  label="Total Orders"
                  type="number"
                  name="totalOrders"
                  value={formData.totalOrders}
                  onChange={handleInputChange}
                  required
                  fullWidth
                  inputProps={{ min: 0 }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  label="Total Payouts (₹)"
                  type="number"
                  name="totalPayouts"
                  value={formData.totalPayouts}
                  onChange={handleInputChange}
                  required
                  fullWidth
                  inputProps={{ step: "0.01", min: 0 }}
                />
              </Grid>

              <Grid item xs={12} md={6} />

              <Grid item xs={12} md={6}>
                <TextField
                  label="Sale From Date"
                  type="date"
                  name="saleFromDate"
                  value={formData.saleFromDate}
                  onChange={handleDateChange}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  label="Sale To Date"
                  type="date"
                  name="saleToDate"
                  value={formData.saleToDate}
                  onChange={handleDateChange}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            </Grid>
          </DialogContent>

          <DialogActions sx={{ p: 2 }}>
            <Button
              variant="outlined"
              onClick={() => {
                setShowModal(false);
                setEditingRecord(null);
                resetForm();
              }}
            >
              Cancel
            </Button>

            <Button type="submit" variant="contained" disabled={loading}>
              {loading ? (
                <>
                  <CircularProgress size={18} sx={{ mr: 1, color: "inherit" }} />
                  {editingRecord ? "Updating..." : "Creating..."}
                </>
              ) : editingRecord ? (
                "Update Record"
              ) : (
                "Create Record"
              )}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>

      <Dialog
        open={showDeleteConfirm}
        onClose={() => !deleteBtnLoading && setShowDeleteConfirm(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle color="error">Confirm Deletion</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mt: 1 }}>
            Are you sure you want to delete this record? This action cannot be
            undone.
          </Alert>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            variant="outlined"
            onClick={() => setShowDeleteConfirm(false)}
            disabled={deleteBtnLoading}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDelete}
            disabled={deleteBtnLoading}
          >
            {deleteBtnLoading ? (
              <>
                <CircularProgress size={18} sx={{ mr: 1, color: "inherit" }} />
                Deleting...
              </>
            ) : (
              "Delete Record"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      <style jsx>{`
        @keyframes spin {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }
      `}</style>
    </Box>
  );
};

export default FoodDeliveryApp;